import React, { useMemo, useState } from "react";
import {
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    LinearProgress,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    IconButton,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import {
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    DocumentResponseDto,
    useReplaceDocumentMutation,
    useUploadDocumentMutation,
} from "../../api/documentsApi";
import { formatFileSize } from "../../utils/fileUtils";

export type DocumentEntityType = "Company" | "Vehicle" | "Trailer" | "employee" | "Claim";

export interface DocumentEntityOption {
    id: number;
    label: string;
}

interface DocumentUploadModalProps {
    open: boolean;
    onClose: () => void;
    entityType: DocumentEntityType;
    entityId?: number;
    entityLabel?: string;
    entityOptions?: DocumentEntityOption[];
    onSuccess?: () => void;
    defaultCategory?: string;
    mode?: "upload" | "replace";
    document?: DocumentResponseDto | null;
}

const DOCUMENT_CATEGORIES = [
    { value: "Insurance", label: "Insurance" },
    { value: "Registration", label: "Registration" },
    { value: "Inspection", label: "DOT Inspection" },
    { value: "Maintenance", label: "Maintenance Records" },
    { value: "License", label: "License/Permits" },
    { value: "Manual", label: "Owner's Manual" },
    { value: "Warranty", label: "Warranty Information" },
    { value: "PoliceReport", label: "Police Report" },
    { value: "Other", label: "Other" },
];

const formatDateInputValue = (value?: string) => {
    if (!value) return "";
    return value.slice(0, 10);
};

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
    open,
    onClose,
    entityType,
    entityId,
    entityLabel,
    entityOptions,
    onSuccess,
    defaultCategory = "",
    mode = "upload",
    document,
}) => {
    const [dragOver, setDragOver] = useState(false);
    const [uploadDocument, { isLoading, error }] = useUploadDocumentMutation();
    const [replaceDocument, { isLoading: isReplacing, error: replaceError }] =
        useReplaceDocumentMutation();
    const isReplaceMode = mode === "replace";
    const isSubmitting = isLoading || isReplacing;
    const requestError = error || replaceError;
    const entityOptionsForSelect = entityOptions ?? [];
    const requiresEntitySelection = entityOptions !== undefined && !isReplaceMode;
    const resolvedEntityLabel = entityLabel ?? entityType;

    const validationSchema = useMemo(
        () =>
            Yup.object({
                file: isReplaceMode
                    ? Yup.mixed().nullable().optional()
                    : Yup.mixed().nullable().required("Please select a file to upload"),
                entityId: requiresEntitySelection
                    ? Yup.number().required(`Please select a ${resolvedEntityLabel.toLowerCase()}`)
                    : Yup.mixed().nullable().optional(),
                documentCategory: Yup.string().optional(),
                description: Yup.string().optional(),
                isPermit: Yup.boolean(),
                isActive: Yup.boolean(),
                expirationDate: Yup.string()
                    .optional()
                    .test(
                        "future-date",
                        "Expiration date must be in the future",
                        (value) => {
                            if (!value) return true;
                            const selectedDate = new Date(value);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            return selectedDate > today;
                        }
                    ),
            }),
        [isReplaceMode, requiresEntitySelection, resolvedEntityLabel]
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            file: null as File | null,
            entityId: document?.entityId ?? entityId ?? "",
            documentCategory: document?.documentCategory ?? defaultCategory,
            description: document?.description ?? "",
            isPermit: document?.isPermit ?? false,
            isActive: document?.isActive ?? true,
            expirationDate: formatDateInputValue(document?.expirationDate),
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                if (isReplaceMode) {
                    if (!document) return;

                    await replaceDocument({
                        id: document.id,
                        file: values.file,
                        documentCategory: values.documentCategory || undefined,
                        description: values.description || undefined,
                        isPermit: values.isPermit,
                        isActive: values.isActive,
                        expirationDate: values.expirationDate || undefined,
                    }).unwrap();
                } else {
                    if (!values.file) return;
                    const selectedEntityId = entityType === "Company" ? 0 : Number(values.entityId || entityId);
                    if (!selectedEntityId && entityType !== "Company") return;

                    await uploadDocument({
                        file: values.file,
                        entityType,
                        entityId: selectedEntityId,
                        documentCategory: values.documentCategory || undefined,
                        description: values.description || undefined,
                        isPermit: values.isPermit,
                        isActive: values.isActive,
                        expirationDate: values.expirationDate || undefined,
                    }).unwrap();
                }

                onSuccess?.();
                handleClose();
            } catch (error) {
                console.error("Document save failed:", error);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        setDragOver(false);
        onClose();
    };

    const handleFileSelect = (file: File) => {
        formik.setFieldValue("file", file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const removeFile = () => {
        formik.setFieldValue("file", null);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">
                        {isReplaceMode ? "Edit / Replace Document" : `Upload ${resolvedEntityLabel} Document`}
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    {requestError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {(requestError as any)?.data?.message || "Failed to save document. Please try again."}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        {requiresEntitySelection && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={entityOptionsForSelect}
                                    value={
                                        entityOptionsForSelect.find(
                                            (option) => option.id === Number(formik.values.entityId)
                                        ) ?? null
                                    }
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(_event, option) => {
                                        formik.setFieldValue("entityId", option?.id ?? "");
                                    }}
                                    onBlur={() => formik.setFieldTouched("entityId", true)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={resolvedEntityLabel}
                                            error={formik.touched.entityId && Boolean(formik.errors.entityId)}
                                            helperText={
                                                formik.touched.entityId && formik.errors.entityId
                                                    ? String(formik.errors.entityId)
                                                    : undefined
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* File Upload Area */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                {isReplaceMode ? "Replace File (Optional)" : "Select File"}
                            </Typography>

                            {!formik.values.file ? (
                                <Paper
                                    sx={{
                                        border: `2px dashed ${dragOver ? "primary.main" : "grey.300"}`,
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        backgroundColor: dragOver ? "action.hover" : "background.paper",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            backgroundColor: "action.hover",
                                        },
                                    }}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => window.document.getElementById("file-input")?.click()}
                                >
                                    <UploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                                    <Typography variant="body1" gutterBottom>
                                        {isReplaceMode
                                            ? "Drop a new file here or click to browse"
                                            : "Drop files here or click to browse"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {isReplaceMode && document
                                            ? `Leave empty to keep ${document.originalFileName}`
                                            : "Supports PDF, images, documents and other file types"}
                                    </Typography>
                                    <input
                                        id="file-input"
                                        type="file"
                                        hidden
                                        onChange={handleFileInputChange}
                                        accept="*/*"
                                    />
                                </Paper>
                            ) : (
                                <Paper sx={{ p: 2, border: "1px solid", borderColor: "success.main" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <AttachFileIcon color="success" />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {formik.values.file.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatFileSize(formik.values.file.size)}
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={removeFile} color="error">
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            )}

                            {formik.touched.file && formik.errors.file && (
                                <Typography variant="caption" color="error">
                                    {formik.errors.file}
                                </Typography>
                            )}
                        </Grid>

                        {/* Document Category and Expiration Date */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Document Category</InputLabel>
                                <Select
                                    name="documentCategory"
                                    value={formik.values.documentCategory}
                                    onChange={formik.handleChange}
                                    label="Document Category"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {DOCUMENT_CATEGORIES.map((category) => (
                                        <MenuItem key={category.value} value={category.value}>
                                            {category.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="expirationDate"
                                label="Expiration Date (Optional)"
                                type="date"
                                size="small"
                                value={formik.values.expirationDate}
                                onChange={formik.handleChange}
                                error={formik.touched.expirationDate && Boolean(formik.errors.expirationDate)}
                                helperText={formik.touched.expirationDate && formik.errors.expirationDate}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        {/* Document Options Checkboxes */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Document Options
                            </Typography>
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="isPermit"
                                            checked={formik.values.isPermit}
                                            onChange={formik.handleChange}
                                            color="primary"
                                        />
                                    }
                                    label="This is a permit document"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="isActive"
                                            checked={formik.values.isActive}
                                            onChange={formik.handleChange}
                                            color="success"
                                        />
                                    }
                                    label="Document is active"
                                />
                            </Box>
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="description"
                                label="Description (Optional)"
                                placeholder="Enter a description for this document..."
                                multiline
                                rows={3}
                                size="small"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={formik.touched.description && formik.errors.description}
                            />
                        </Grid>
                    </Grid>

                    {isSubmitting && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" gutterBottom>
                                {isReplaceMode ? "Saving document..." : "Uploading document..."}
                            </Typography>
                            <LinearProgress />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            isSubmitting ||
                            (!isReplaceMode && !formik.values.file) ||
                            (requiresEntitySelection && !formik.values.entityId)
                        }
                        startIcon={<UploadIcon />}
                    >
                        {isSubmitting
                            ? isReplaceMode
                                ? "Saving..."
                                : "Uploading..."
                            : isReplaceMode
                                ? "Save Document"
                                : "Upload Document"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default DocumentUploadModal;
